import { useState, type ReactNode } from 'react';
import { TOS_TEXT, TOS_VERSION } from '@siliconx/shared';
import logoLight from '@siliconx/shared/brand/logo-light.svg';
import logoDark from '@siliconx/shared/brand/logo-dark.svg';
import { PrivacyPolicyContent } from '../legal/PrivacyPolicyContent';

/**
 * apps/customer/src/components/shell/SignupFrame.tsx
 *
 * SIL-1057: branded frame for the public /signup page (routes/signup.tsx).
 * Wraps every signup step (loading, invalid, form, verify, success) so a
 * referral-link visitor sees the SiliconX logo + wordmark, a card matching
 * the portal's other unauthenticated screens (auth/callback.tsx, tos.tsx —
 * see siliconx-design-system skill), and a footer with Sign in / Terms /
 * Privacy — same look as the Cognito managed login branding
 * (infra/modules/cognito-client/managed_login_branding.tf).
 *
 * Deliberately NOT AppShell: /signup stays public (no session, no nav) — see
 * routes/signup.tsx's own docstring (__root.tsx's isPublicSignupPath renders
 * this route with no AuthProvider/AppShell at all, since both mount
 * session-dependent queries that would 401 and bounce an anonymous visitor
 * to the Hosted UI). This is a standalone frame, not a slimmed-down AppShell.
 *
 * Terms/Privacy are IN-PAGE PANELS, not links: this predates the
 * 2026-09-20 fix that moved /tos and /privacy into __root.tsx's public
 * route group (isPublicPortalPath, alongside /signup) — before that, both
 * sat behind the auth gate and a signed-out referral visitor who followed
 * either link got bounced to the Cognito Hosted UI mid signup, losing the
 * flow entirely. Both routes are genuinely reachable read-only without a
 * session now, but the in-page panels stay: they satisfy the same
 * "reach the content without losing in-progress form input" requirement
 * with zero navigation at all, which is simpler than a `target="_blank"`
 * link for this single-purpose form. Terms text comes straight from the
 * shared TOS_TEXT/TOS_VERSION constants (packages/shared/src/dto/tos.ts —
 * the same source /tos renders for a signed-out visitor); Privacy reuses
 * PrivacyPolicyContent, the same static JSX routes/privacy.tsx renders.
 * Only "Sign in" stays a real link — it is meant to leave /signup.
 */
export function SignupFrame({
  children,
}: {
  // SIL-1059: children may also be a render function receiving openTerms
  // so the ToS clickwrap checkbox's "Terms of Service" label link can open
  // this same in-page panel instead of duplicating it.
  children: ReactNode | ((api: { openTerms: () => void }) => ReactNode);
}) {
  // Same pattern as AppShell's logo swap (src/lib/theme.ts): the initial
  // value comes from the <html class="dark"> the pre-paint script
  // (index.html) sets synchronously before any JS runs, so there's no
  // mismatch flash. No live theme toggle on this public page — a
  // referral-link visitor has no session to persist a pin to; whatever the
  // stored/OS preference resolved to at load time is what renders here.
  const [dark] = useState(() => document.documentElement.classList.contains('dark'));
  // Toggles an in-page panel below the card instead of navigating — see
  // the docstring above for why /tos and /privacy can't be real links here.
  const [legalPanel, setLegalPanel] = useState<'terms' | 'privacy' | null>(null);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4 sm:p-8">
      <div className="w-full max-w-md space-y-6">
        <div className="flex justify-center">
          <a href="/dashboard" aria-label="SiliconX home">
            <img
              src={dark ? logoDark : logoLight}
              alt="SiliconX"
              className="h-8 w-auto max-w-[220px] object-contain"
            />
          </a>
        </div>

        <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
          {typeof children === 'function' ? children({ openTerms: () => setLegalPanel('terms') }) : children}
        </div>

        {legalPanel && (
          <div className="rounded-xl border border-border bg-card p-4 shadow-sm" data-testid="signup-legal-panel">
            <div className="mb-2 flex items-center justify-between gap-3">
              <h2 className="text-sm font-semibold text-foreground">
                {legalPanel === 'terms' ? `Terms of Service (version ${TOS_VERSION})` : 'Privacy Policy'}
              </h2>
              <button
                type="button"
                onClick={() => setLegalPanel(null)}
                className="text-xs text-brand-600 underline"
              >
                Close
              </button>
            </div>
            <div className="max-h-64 overflow-y-auto text-xs text-muted-foreground">
              {legalPanel === 'terms' ? (
                <pre className="whitespace-pre-wrap font-sans">{TOS_TEXT}</pre>
              ) : (
                <div className="space-y-3">
                  <PrivacyPolicyContent />
                </div>
              )}
            </div>
          </div>
        )}

        <div className="flex flex-col items-center gap-2 text-center text-xs text-muted-foreground">
          <p>
            Already have an account?{' '}
            <a href="/dashboard" className="text-brand-600 underline">
              Sign in
            </a>
          </p>
          <p className="space-x-3">
            <button
              type="button"
              data-track="signup_terms_panel"
              onClick={() => setLegalPanel((p) => (p === 'terms' ? null : 'terms'))}
              className="underline hover:text-foreground"
            >
              Terms of Service
            </button>
            <span aria-hidden="true">&middot;</span>
            <button
              type="button"
              data-track="signup_privacy_panel"
              onClick={() => setLegalPanel((p) => (p === 'privacy' ? null : 'privacy'))}
              className="underline hover:text-foreground"
            >
              Privacy Policy
            </button>
            <span aria-hidden="true">&middot;</span>
            {/* SIL-1529: signup has no AppShell footer; the shared analytics
                module opens the HubSpot banner on any data-cookie-settings click. */}
            <button
              type="button"
              data-cookie-settings=""
              data-track="signup_cookie_settings"
              className="underline hover:text-foreground"
            >
              Cookie settings
            </button>
            <span aria-hidden="true">&middot;</span>
            <a href="/terms" target="_blank" rel="noreferrer" className="underline hover:text-foreground">
              Terms &amp; SMS Terms
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
